// helpdesk-frontend/src/hooks/useSzerokoscElementu.js
// Pomiar SZEROKOŚCI elementu w px, odświeżany przy zmianie rozmiaru elementu i okna.
//
// Powstał dla osi czasu (Gantt): oś dostaje szerokość w px liczoną jako
// liczba_dni × piksele_na_dzień, więc żeby wypełniła kontener „w 100%", trzeba tę
// szerokość ZMIERZYĆ — CSS-em się tego nie policzy, bo pozycje słupków i siatki
// idą stylem inline. Wzorzec obserwatora jest ten sam co w usePopoverPosition.js.
//
// ⚠️ Pomiar wchodzi przez CALLBACK REF (a nie useEffect), bo element osi znika i wraca
// przy przełączaniu widoku (wykres ↔ lista, stan pusty). Zwykły `useRef` + `useEffect`
// nie dowiedziałby się o ponownym zamontowaniu i oś zostałaby ze starym pomiarem.

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * @param {object|null} refZewnetrzny  opcjonalny `useRef`, do którego przepiszemy element
 *        (potrzebny, gdy ten sam element trzeba też przewijać — np. `scrollLeft` osi).
 *        ⚠️ MUSI być stabilny (obiekt z `useRef`), inaczej ref-callback zmienia tożsamość
 *        co render i React odpina/przypina go w kółko.
 * @param {object} opcje  `pomiar`: "client" (domyślnie — szerokość widoczna, bez paska
 *        przewijania i bez obramowań) albo "outer" (`offsetWidth` — z obramowaniami,
 *        czyli tyle, ile element ZAJMUJE w układzie).
 * @returns {[function, number, function]} [ref-callback, szerokość w px, wymuś pomiar]
 */
export default function useSzerokoscElementu(refZewnetrzny = null, { pomiar = "client" } = {}) {
    const [szerokosc, setSzerokosc] = useState(0);
    const elRef = useRef(null);
    const obsRef = useRef(null);

    const zmierz = useCallback(() => {
        const el = elRef.current;
        if (!el) return;
        const w = Math.max(0, Math.round((pomiar === "outer" ? el.offsetWidth : el.clientWidth) || 0));
        setSzerokosc((poprzednia) => (poprzednia === w ? poprzednia : w));
    }, [pomiar]);

    const ustawElement = useCallback((el) => {
        if (obsRef.current) {
            obsRef.current.disconnect();
            obsRef.current = null;
        }
        elRef.current = el;
        if (refZewnetrzny) refZewnetrzny.current = el;
        // Przy odpięciu ZOSTAWIAMY ostatni pomiar — zerowanie dawałoby przy przełączaniu
        // widoku klatkę z osią „do połowy", a i tak mierzymy ponownie po przypięciu.
        if (!el) return;
        zmierz();
        if (typeof ResizeObserver === "function") {
            const obs = new ResizeObserver(zmierz);
            obs.observe(el);
            obsRef.current = obs;
        }
    }, [refZewnetrzny, zmierz]);

    // Zmiana rozmiaru okna — jedyne źródło pomiaru tam, gdzie nie ma ResizeObserver
    // (starsze przeglądarki, środowisko testowe).
    useEffect(() => {
        window.addEventListener("resize", zmierz);
        return () => window.removeEventListener("resize", zmierz);
    }, [zmierz]);

    useEffect(() => () => {
        if (obsRef.current) {
            obsRef.current.disconnect();
            obsRef.current = null;
        }
    }, []);

    return [ustawElement, szerokosc, zmierz];
}
